import React from 'react';

import AlarmIcon from '@mui/icons-material/Alarm';
import ListAlt from '@mui/icons-material/ListAlt';
import { ActionItemKeys, FormProgress } from '@uplift/api';
import { trackToDoCTA, useActionItem, useL10n } from '@uplift/hooks';
import { UserRole } from '@uplift/types';
import { LinkDashboardActionItem } from '@uplift/ui-v2';

const intakeFormKeys = [
  'upliftStandard',
  'couplesIntake',
  'psychIntake',
  'familyIntake'
];

const getIntakeForm = (forms: Record<string, FormProgress>): FormProgress | undefined => {
  const potentials = Object.keys(forms).filter(x => intakeFormKeys.includes(x));

  if (!potentials.length) {
    return;
  }

  return forms[potentials[0]];
};

type AssessmentProgress = FormProgress & { formKey: string };

export const useFormProgressActionItem = () => {
  const l10n = useL10n();
  const formProgressData = useActionItem(ActionItemKeys.CLIENT_FORM_PROGRESS);

  const intakeFormActionItem = React.useMemo(() => {
    if (formProgressData.isLoading) {
      return null;
    }

    const intakeProgress: FormProgress | undefined = getIntakeForm(formProgressData.forms);

    if (!intakeProgress || intakeProgress.completed) {
      return;
    }

    const title = intakeProgress.progress
      ? l10n.dashboard.actionItems.intakeForm.title(intakeProgress?.progress)
      : l10n.dashboard.actionItems.intakeFormEmpty.title;

    return (
      <LinkDashboardActionItem
        title={title}
        color="primary"
        icon={<ListAlt />}
        link={`/form/submission/${intakeProgress.submissionId}`}
        actionText="Finish Intake"
        key="intake"
        onClick={() =>
          trackToDoCTA({
            toDoType: ActionItemKeys.CLIENT_FORM_PROGRESS,
            userType: UserRole.patient
          })
        }
      />
    );
  }, [ formProgressData, l10n.dashboard.actionItems.intakeForm, l10n.dashboard.actionItems.intakeFormEmpty.title ]);

  const assessmentItems = React.useMemo(() => {
    if (formProgressData.isLoading) {
      return [];
    }

    const assessments: AssessmentProgress[] = Object.keys(formProgressData.forms).reduce((acc, cur) => {
      if (intakeFormKeys.includes(cur)) {
        return acc;
      }

      return [
        ...acc,
        {
          formKey: cur,
          ...formProgressData.forms[cur]
        }
      ];
    }, []);

    return assessments
      .filter(x => !x.completed && !x.expired && x.submissionId)
      .map(x => (
        <LinkDashboardActionItem
          title={l10n.dashboard.actionItems.assessment.title}
          color="secondary"
          icon={<AlarmIcon />}
          link={`/form/submission/${x.submissionId}`}
          actionText="Check in"
          key={x.submissionId}
          onClick={() =>
            trackToDoCTA({
              toDoType: ActionItemKeys.CLIENT_FORM_PROGRESS,
              userType: UserRole.patient
            })
          }
        />
      ));
  }, [ formProgressData.forms, formProgressData.isLoading, l10n.dashboard.actionItems.assessment.title ]);

  const actionItem = React.useMemo(() => {
    if (formProgressData.isLoading) {
      return null;
    }

    const result: React.ReactNode[] = [
      intakeFormActionItem,
      assessmentItems?.[0]
    ].filter(x => x);

    if (!result.length) {
      return null;
    }

    return result;
  }, [ assessmentItems, formProgressData.isLoading, intakeFormActionItem ]);

  return {
    actionItem,
    isLoading: formProgressData.isLoading
  };
};
